import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { actionsCreators, State } from "../../redux";
import { Product, Track } from "client-v2";
import ReactPixel from "react-facebook-pixel";

import Info from "./steps/Info";
import Header from "./steps/Header";
import Container from "../../components/container/Container";
import SiteFooter from "../../components/container/SiteFooter";
import Resume from "./steps/Resume";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import styled from "styled-components";
import media from "styled-media-query";
import CurrencySelector from "../../components/selector/CurrencySelector";
import { ColorV2 } from "@adoptaunabuelo/react-components";

const ProcessOnlyContent = styled.div`
	display: flex;
	width: 100%;
	justify-content: center;
	padding-top: 24px;

	${media.lessThan("medium")`
		padding-top: 0;
	`}
`;

const DonationWizard = (props: Props) => {
	const view = "donation";
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { setShowSuccess } = bindActionCreators(actionsCreators, dispatch);
	const appLocation = useSelector((state: State) => state.location.location);
	const currentUser = useSelector((state: State) => state.user.currentUser);
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const isLandingMobile = isMobile && !props.processOnly;
	const [selectedPrice, setSelectedPrice] = useState(15);
	const [selectedCharge, setSelectedCharge] = useState("monthly");
	const [hasPriceError, setHasPriceError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [utmData, setUtmData] = useState<{
		source?: string;
	}>({});

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const utmSource = params.get("utm_source");
		setUtmData({
			source: utmSource ? utmSource : undefined,
		});

		//Track to Mixpanel
		Track.event({
			event: "donation detail view init",
			user: currentUser,
			params: {
				view: "donation",
			},
		});
	}, []);

	const onShareClick = (op: any) => {
		const url = "https://adoptaunabuelo.org/donacion/";
		if (op.id === "twitter") {
			window.open("http://www.twitter.com/share?url=" + url, "_blank");
		} else if (op.id === "facebook") {
			window.open("http://www.facebook.com/sharer.php?u=" + url);
		} else if (op.id === "whatsapp") {
			window.open("whatsapp://send?text=" + url);
		} else if (op.id === "url") {
			navigator.clipboard.writeText(url);
			setShowSuccess({
				show: true,
				message: "Se ha copiado el enlace en tu portapapeles",
			});
		}
	};

	const onNextClick = () => {
		//No donar si el importe es inválido (por debajo del mínimo) o vacío
		if (hasPriceError || !selectedPrice) return;

		Track.event({
			event: "donation progress init",
			user: currentUser,
			params: {
				view: view,
			},
		});

		if (selectedCharge === "unique") {
			//Track on Mixpanel
			Track.event({
				event: "donation product selected",
				user: currentUser,
				params: {
					Product: "one-off",
					amount: selectedPrice,
					view: view,
				},
			});

			navigate("/donacion/registro", {
				state: {
					objectId: "unique",
					charge: "unique",
					price: selectedPrice,
				},
			});
			return;
		}

		setLoading(true);
		Product.get({
			type: "subscription",
			price: selectedPrice,
			charge: selectedCharge,
			currency: appLocation ? appLocation.currency.name : "eur",
		})
			.then((result) => {
				const product = result.data[0];

				//Track on Mixpanel
				Track.event({
					event: "donation product selected",
					user: currentUser,
					params: {
						Product: product.objectId,
						amount: selectedPrice,
						view: view,
					},
				});

				//Track on Facebook
				ReactPixel.trackCustom("InitiateCheckout", {
					value: product.price,
					currency: appLocation ? appLocation.currency.name : "eur",
					product_id: product.objectId,
					product_name: product.title,
					customer_type: product.charge,
				});

				setLoading(false);
				navigate("/donacion/registro", {
					state: product,
				});
			})
			.catch((e) => {
				setLoading(false);
			});
	};

	const onMobileLandingClick = () => {
		navigate(`/proceso-donacion/${window.location.search}`);
	};

	const handlePriceChange = (price: number, charge: string) => {
		setSelectedPrice(price);
		setSelectedCharge(charge);
	};

	const resumeNode = (
		<Resume
			loading={loading}
			onPriceChange={handlePriceChange}
			onErrorChange={setHasPriceError}
			onClick={onNextClick}
			onShareClick={onShareClick}
		/>
	);

	return (
		<>
			<Container
				style={{
					backgroundColor:
						props.processOnly && isMobile
							? ColorV2.surface.background
							: "white",
				}}
				contentStyle={
					isMobile
						? props.processOnly
							? {
									//La tarjeta de /proceso-donacion es un bottom sheet a
									//pantalla completa (esquinas superiores redondeadas): sin
									//padding lateral va de borde a borde y el inset lo pone el
									//DataView; ademas evita el scroll horizontal en movil.
									padding: 0,
									width: "100%",
									boxSizing: "border-box",
								}
							: { paddingTop: 0 }
						: {}
				}
				headerProps={{
					logoBackgroundColor: "transparent",
					childrenPosition: "right",
					contentStyle: isMobile ? undefined : { flex: 1 },
					children: <CurrencySelector />,
				}}
				footerProps={{
					style: isLandingMobile
						? {
								backgroundColor: "white",
								padding: 16,
								borderRight: "1px solid white",
							}
						: { backgroundColor: "transparent" },
					buttonProps: {
						style: {
							width: "100%",
						},
						children: isLandingMobile
							? "Quiero ser embajador"
							: selectedPrice
								? `Quiero donar ${selectedPrice} ${
										appLocation
											? appLocation.currency.symbol
											: "€"
									}${
										selectedCharge === "monthly"
											? " al mes"
											: selectedCharge === "annually"
												? " al año"
												: ""
									}`
								: "Quiero donar",
						loading: loading,
						onClick: isLandingMobile
							? onMobileLandingClick
							: onNextClick,
					},
				}}
				RightView={props.processOnly ? undefined : resumeNode}
				fullBleedContent={!props.processOnly}
			>
				{props.processOnly ? (
					<ProcessOnlyContent>
						<Resume
							style={{
								width: "min(100%, 520px)",
								...(isMobile
									? {
											boxShadow: "none",
											border: "none",
										}
									: {}),
							}}
							loading={loading}
							onPriceChange={handlePriceChange}
							onErrorChange={setHasPriceError}
							onClick={onNextClick}
							onShareClick={onShareClick}
						/>
					</ProcessOnlyContent>
				) : (
					<>
						<Header utmSource={utmData.source}>
							{!isMobile && (
								<Resume
									style={{
										width: "unset",
									}}
									loading={loading}
									onPriceChange={handlePriceChange}
									onErrorChange={setHasPriceError}
									onClick={onNextClick}
									onShareClick={onShareClick}
								/>
							)}
						</Header>
						<Info price={selectedPrice} />
					</>
				)}
			</Container>
			{!props.processOnly && <SiteFooter />}
		</>
	);
};

export default DonationWizard;
export interface Props {
	processOnly?: boolean;
}
